import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {Field} from '../../interfaces/field';
import {Subscription} from 'rxjs';

import {StateService} from '../../services/state.service';
import {FieldErrorService} from '../../services/field-error.service';
import {faChevronDown, faChevronUp, faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {AddressFieldComponent} from "../address-field/address-field.component";
import {SortableAddressFieldComponent} from "../sortable-address-field/sortable-address-field.component";
import {TranslateService} from "@ngx-translate/core";

import {getRecentPassengers} from '../../services/recent-passengers';

declare const window: any;

@Component({
    selector: 'app-field-wrapper',
    templateUrl: './field-wrapper.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./field-wrapper.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FieldWrapperComponent implements OnInit, OnDestroy {
  @Input() field: Field | any;
  @Input() index: number;
  @Input() summary = false;
  @Input() returnTrip = false;
  @Input() layout = 'page-layout';
  @Input() form: any = {config: {parts: []}};
  @Input() minimumOrderTime = 60;
  @Input() mainConfig;
  @Input() bookingHourlyDestination;
  @Input() part:any;
  @Input() hiddenFieldTitles!: boolean;

  @Output() openHelpModal: EventEmitter<any> = new EventEmitter<any>();
  @Output() checkWorkArea: EventEmitter<any> = new EventEmitter<any>();
  @Input() formGroup: UntypedFormGroup;
  @ViewChild(AddressFieldComponent) addressField: AddressFieldComponent;
  @ViewChild(SortableAddressFieldComponent) sortableAddressField: SortableAddressFieldComponent;
  orderMode: string;
  orderModeSub: Subscription;
  airportDeparture: boolean;
  airportDepartureSub: Subscription;
  airportDestination: boolean;
  airportDestinationSub: Subscription;

  opened: boolean;
  private static passengerListCounter = 0;
  recentPassengers: any[] = [];
  passengerListId = `wb-recent-passengers-${++FieldWrapperComponent.passengerListCounter}`;
  hasErrors = false;
  formChanges: Subscription;
  faInfoCircle = faInfoCircle;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  private languageLoaded: boolean = false;
  protected cleanLabel:string;
  @Input() activeTab!: string;

  constructor(@Inject(DOCUMENT) private document: Document,
              private state: StateService,
              private translate: TranslateService,
              private errorService: FieldErrorService,
              private changeDetection: ChangeDetectorRef) {
    const _self = this;
    this.opened = false;
    this.orderModeSub = this.state.orderMode$.subscribe(
      orderMode => this.orderMode = orderMode
    );

    if(typeof this.state.airportDeparture === 'undefined') {
      this.airportDepartureSub = this.state.airportDeparture$.subscribe(
        (airportDeparture) => {
          if (_self.field && _self.field.property) {
            if (_self.returnTrip) {
              _self.airportDestination = airportDeparture;
            } else {
              _self.airportDeparture = airportDeparture;
            }
          }
        });
      this.airportDestinationSub = this.state.airportDestination$.subscribe((airportDestination) => {
        if (_self.field && _self.field.property) {
          if (_self.returnTrip) {
            _self.airportDeparture = airportDestination;
          } else {
            _self.airportDestination = airportDestination;
          }
        }
      });
    }
  }

  ngOnInit() {
    if (this.field?.type !== 'collapse') {
      this.formChanges = this.formGroup.valueChanges.subscribe(this.setFieldState.bind(this));
    } else {
      this.opened = this.field?.opened;
    }
    this.cleanLabel = this.field?.label?.replace('_label', '').replace(/_/g, '-').replace(/ /g, '');
    if(!this.field?.label) {
      this.cleanLabel = this.field?.property?.replace('_label', '').replace(/_/g, '-').replace(/ /g, '');
    }

    if (this.field?.translations && this.translate) {
      this.loadLanguages(); // initial (no seeding, no recursion)

      this.translate.onLangChange?.subscribe(() => {
        this.loadLanguages(); // re-apply on actual lang switch only
      });

      if (!this.field.label && !this.languageLoaded) {
        setTimeout(() => { if (!this.languageLoaded) this.loadLanguages(); }, 100);
      }
    }

    if (typeof this.state?.airportDeparture !== 'undefined') {
      if (this.returnTrip) {
        this.airportDestination = this.state?.airportDeparture;
        this.airportDeparture   = this.state?.airportDestination;
      } else {
        this.airportDeparture   = this.state?.airportDeparture;
        this.airportDestination = this.state?.airportDestination;
      }
    }

    if (this.field?.property === 'firstName' && !this.summary) {
      this.recentPassengers = getRecentPassengers().filter(p => p.firstName);
    }

    this.setFieldState(this.formGroup.getRawValue());
  }

  /**
   * The first-name input suggests previously booked passengers (datalist, labelled
   * "First Last" so people sharing a first name stay distinguishable). When the typed value
   * is exactly one of the suggestions — i.e. the user picked it — set the first name back to
   * just the first name and fill the passenger's other empty fields.
   */
  passengerLabel(p: any): string {
    return [p.firstName, p.lastName].filter(Boolean).join(' ');
  }

  applyRecentPassenger(event: Event): void {
    const typed = String((event.target as HTMLInputElement).value || '').trim().toLowerCase();
    if (!typed || (event as InputEvent).inputType === 'insertText') return;
    const match = this.recentPassengers.find(p => this.passengerLabel(p).toLowerCase() === typed);
    if (!match) return;
    const patch: any = {firstName: match.firstName};
    Object.keys(match).forEach(k => {
      const control = this.formGroup.controls[k];
      if (k !== 'firstName' && control && !control.value) patch[k] = match[k];
    });
    this.formGroup.patchValue(patch);
  }

  loadLanguages() {
    if (!this.translate || !this.field?.translations) return;
    const preferred = this.state?.language;
    const available = Object.keys(this.field.translations);
    if (!available.length) return;

    const lang = preferred;
    let t = this.field.translations[lang];
    if (!t || !Object.keys(t).length) {
      // state.language uses locale format (e.g. 'en-EN') but field.translations may use
      // short codes (e.g. 'en'). Try matching on the language part before the dash.
      const shortCode = lang?.split('-')[0]?.toLowerCase();
      if (shortCode) {
        t = this.field.translations[shortCode];
      }
    }
    if (!t || !Object.keys(t).length) {
      const firstLang = Object.keys(this.field.translations)[0];
      t = this.field.translations[firstLang];
      console.warn(`Missing translation for '${preferred}', using fallback '${firstLang}'`);
    }

    const id = this.field.id;
    const labelKey       = `label_${id}`;
    const placeholderKey = `placeholder_${id}`;
    const helpKey        = `helptext_${id}`;

    // Build what we *want* to merge
    const bag: Record<string, string> = {};
    if (t.label)       bag[labelKey]       = t.label;
    if (t.placeholder) bag[placeholderKey] = t.placeholder;
    if (t.helptext)    bag[helpKey]        = t.helptext;
    if (t.singularSelect) bag[`singularSelect_${id}`] = t.singularSelect;
    if (t.pluralSelect)   bag[`pluralSelect_${id}`]   = t.pluralSelect;

    if (Array.isArray(t.options) && t.options.length) {
      for (let i = 0; i < t.options.length; i++) {
        const opt = t.options[i];
        if (opt?.label) bag[`option_${id}_${i + 1}`] = opt.label;
      }
    }

    // Only call setTranslation if we actually add/override something
    const existing = ((this.translate as any).translations?.[lang]) || {};
    let hasDiff = false;
    for (const k of Object.keys(bag)) {
      if (existing[k] !== bag[k]) { hasDiff = true; break; }
    }
    if (hasDiff) {
      // Merge our keys into the current lang without seeding (avoids recursion)
      this.translate.setTranslation(lang, bag, /* merge */ true);
    }

    // Point field props at the dynamic keys
    if (t.label)       this.field.label       = labelKey;
    if (t.placeholder) this.field.placeholder = placeholderKey;
    if (t.helptext)    this.field.helpText    = helpKey;

    this.languageLoaded = true;
    // Defer translate.use() past the current CD cycle.
    // setTranslation() (above) already populates translations[lang] synchronously,
    // which causes translate.use(lang) to call changeLang() synchronously → NG0100.
    setTimeout(() => this.translate.use(lang), 0);
  }


  setFieldState(values) {
    const self = this;
    if (!self.returnTrip) {
      self.airportDeparture = this.state.airportDeparture;
      self.airportDestination = this.state.airportDestination;
    } else {
      self.airportDeparture = this.state.airportDestination;
      self.airportDestination = this.state.airportDeparture;
    }
    if (self.field.type === 'collapse' || self.field.type === 'sortable-address') {
      self.opened = (self.field.opened ? self.field.opened : !self.field.dynamic);
    } else if (self.formGroup.controls[self.field.property]) {
      if (self.field.showIf) {
        // console.log(`----------------------------------------`);
        let shouldOpen = true;
        /**
         * We need to loop through the showIf object to see if we should open the field
         * Each of the options should be true
         */

        Object.keys(self.field.showIf).forEach((key) => {
          if(!shouldOpen) return;
          // console.log(`----------------------------------------`);
          // console.log(`self.field.showIf`, self.field.showIf);
          // console.log(`key`, key);
          if(key === 'orderMode' && !self.orderMode) return;
          if (key === 'addressAirport' && self.field.showIf[key]) {
            if (self.field.showIf[key] === 'any' &&
              (self.airportDeparture || self.airportDestination)) {
              shouldOpen = true;
            } else if (self.field.showIf[key] === 'departure' &&
              (self.airportDeparture || (values.returnTrip && self.airportDestination))) {
              shouldOpen = true;
            } else if (self.field.showIf[key] === 'destination' &&
              (self.airportDestination || (values.returnTrip && self.airportDeparture))) {
              shouldOpen = true;
            } else {
              shouldOpen = false;
            }
          } else if (key === 'product' && Array.isArray(self.field.showIf[key])) {
            const selectedProductId = values.product && values.product.productId;
            shouldOpen = !!selectedProductId && self.field.showIf[key].includes(selectedProductId);
          } else if (key === 'productSku' && self.field.showIf[key]) {
            // A single sku may be configured as a plain string; normalise so both shapes work.
            // Matched case-insensitively: skus are hand-typed in the portal config, while the
            // product's own sku is upper-case (OrderComponent lowercases it for translation tags).
            const configured = self.field.showIf[key];
            const expectedSkus = (Array.isArray(configured) ? configured : [configured]).map((sku) => String(sku).toLowerCase());
            const selectedSku = values.product && values.product.sku;
            shouldOpen = !!selectedSku && expectedSkus.includes(String(selectedSku).toLowerCase());
          } else if (this[key] && this[key] === self.field.showIf[key]) {
            shouldOpen = true;
            // console.log(`Opening field '${self.field.property}' cause it was found in 'this'`);
          } else if (values[key] && values[key] === self.field.showIf[key]) {
            shouldOpen = true;
            // console.log(`Opening field '${self.field.property}' cause it was found in field values`);
          } else {
            shouldOpen = false;
            // console.log(`shouldOpen:`, shouldOpen);
          }
        });
        self.opened = shouldOpen;

        if (!shouldOpen && self.formGroup.controls[self.field.property].value !== self.field.default) {
          // console.log(`Restoring default for field '${self.field.property}'`);
          // self.formGroup.controls[self.field.property].setValue(self.field.default);
        }
        // console.log(`field ${self.field.property} is ` + (self.opened ? 'opened' : 'closed'));
        // console.log('------');
      } else {
        self.opened = true;
      }
    }
    if (self.changeDetection) {
      self.changeDetection.detectChanges();
    }
    if ((self.field.type === 'text' || self.field.type === 'checkbox' || self.field.type === 'select' || self.field.type === 'email') && self.field.required) {
      if (self.formGroup.controls[self.field.property].value !== '' && self.errorService.fieldHasErrors(self.field.property)) {
        self.errorService.removeError(`${self.field.property}Required`);
      }
    }
  }

  toggleSection(id: string) {
    // console.log(`toggleSection(${id})`);
    this.opened = !this.opened;
    const section = this.document.getElementById(id);
    setTimeout(() => {
      window.scroll({
        behavior: 'smooth',
        left: 0,
        top: section.offsetTop
      });
    }, 300);
  }

  toggleHelpModal(helpTitleTag: string, helpTextTag: string, type?: string, meta?: any) {
    this.openHelpModal.emit({
      helpTitleTag,
      helpTextTag,
      type,
      meta,
    });
  }

  checkWorkAreaEmit(property) {
    this.checkWorkArea.emit(property);
  }

  validityChanged(valid: boolean) {
    this.hasErrors = valid;
  }

  ngOnDestroy(): void {
    if (this.formChanges) {
      this.formChanges.unsubscribe();
    }
    if (this.orderModeSub) {
      this.orderModeSub.unsubscribe();
    }
    if (this.airportDepartureSub) {
      this.airportDepartureSub.unsubscribe();
    }
    if (this.airportDestinationSub) {
      this.airportDestinationSub.unsubscribe();
    }
  }

  addError(error: any) {
    if(this.sortableAddressField) {
      this.sortableAddressField.addErrors(error);
      this.sortableAddressField.checkFieldValidity();
    } else if(this.addressField) {
      this.addressField.errors.addError(error);
    }
  }

  removeError(error: any) {
    if(this.sortableAddressField) {
      this.sortableAddressField.removeErrors(error);
      this.sortableAddressField.checkFieldValidity();
    } else if(this.addressField) {
      this.addressField.errors.removeError(error.id);
    }
  }
}
